import { format, parseISO } from 'date-fns';

export const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';
export const INPUT =
  'rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-claret-500 focus:outline-none focus:ring-1 focus:ring-claret-500 disabled:bg-gray-50 disabled:text-gray-400';
export const PRIMARY =
  'rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:cursor-not-allowed disabled:opacity-50';
export const SECONDARY =
  'rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

// Rules an admin can choose, in the order they're offered. `needs` is the
// content the scope must have for the rule to mean anything.
export const RULES = [
  {
    type: 'content_completed',
    label: 'Content completed',
    help: 'Share of weeks completed in the chosen modules.',
    needs: 'weeks',
  },
  {
    type: 'quizzes_passed',
    label: 'Quizzes passed',
    help: 'Share of quizzes passed.',
    needs: 'quizzes',
  },
  {
    type: 'quiz_average',
    label: 'Quiz average',
    help: 'Average best score of the quizzes taken. Pair it with "Quizzes passed" to require coverage too.',
    needs: 'quizzes',
  },
  {
    type: 'assignments_submitted',
    label: 'Assignments submitted',
    help: 'Share of assignments submitted.',
    needs: 'assignments',
  },
  {
    type: 'assignment_grade',
    label: 'Assignment grade',
    help: 'Average grade of the assignments graded so far. Pair it with "Assignments submitted" to require coverage too.',
    needs: 'assignments',
  },
  {
    type: 'manual_approval',
    label: 'Manual approval',
    help: 'Someone with learner permissions approves each learner.',
    needs: null,
  },
];

export const RULE_BY_TYPE = Object.fromEntries(RULES.map((r) => [r.type, r]));

export const ruleLabel = (type) => RULE_BY_TYPE[type]?.label || type;

const NEEDS_TEXT = {
  weeks: 'weeks of content',
  quizzes: 'quizzes',
  assignments: 'assignments',
};

// Why a rule can't be added for these modules, or null.
export const ruleUnavailable = (type, available) => {
  const needs = RULE_BY_TYPE[type]?.needs;
  if (!needs || !available) return null;
  return available[needs] > 0
    ? null
    : `The chosen modules have no ${NEEDS_TEXT[needs]}.`;
};

export const DEFAULT_TARGET = 80;

export const newRule = (type) =>
  type === 'manual_approval'
    ? { type }
    : {
        type,
        target: DEFAULT_TARGET,
        ...(type === 'quizzes_passed' ? { attempts: 'any' } : {}),
      };

// The editor works on a full copy of the config so a save sends everything.
export const normaliseConfig = (config) => ({
  scope: {
    type: config?.scope?.type || 'core_tracks',
    module_ids: config?.scope?.module_ids ?? [],
  },
  combine: config?.combine || 'all',
  rules: (config?.rules ?? []).map((rule) => ({ ...rule })),
  max_choices: config?.max_choices ?? 1,
  learners_can_change: Boolean(config?.learners_can_change),
  keep_started_on_reset: config?.keep_started_on_reset ?? true,
});

// What the API is sent: module ids only for a module scope, attempts only
// for quizzes passed, no target for approval.
export const configForApi = (config) => ({
  scope:
    config.scope.type === 'modules'
      ? { type: 'modules', module_ids: config.scope.module_ids }
      : { type: 'core_tracks' },
  combine: config.combine,
  rules: config.rules.map((rule) => {
    if (rule.type === 'manual_approval') return { type: rule.type };
    const out = { type: rule.type, target: Number(rule.target) };
    if (rule.type === 'quizzes_passed') out.attempts = rule.attempts || 'any';
    return out;
  }),
  // An empty box means the default of one choice.
  max_choices: Number(config.max_choices) || 1,
  learners_can_change: config.learners_can_change,
  keep_started_on_reset: config.keep_started_on_reset,
});

export const sameConfig = (a, b) =>
  JSON.stringify(configForApi(a)) === JSON.stringify(configForApi(b));

// Settings problems point at a field: "rules[1].target", "rules[1]",
// "scope.module_ids", "max_choices", or "" for the cohort as a whole.
// Group them so each shows next to its setting.
export const groupProblems = (problems = []) => {
  const groups = {
    rules: {},
    scope: [],
    combine: [],
    options: [],
    general: [],
  };
  problems.forEach((problem) => {
    const field = problem.field || '';
    const rule = /^rules\[(\d+)\]/.exec(field);
    if (rule) {
      const index = Number(rule[1]);
      groups.rules[index] = [...(groups.rules[index] ?? []), problem];
    } else if (field.startsWith('scope')) {
      groups.scope.push(problem);
    } else if (field === 'combine') {
      groups.combine.push(problem);
    } else if (
      ['max_choices', 'learners_can_change', 'keep_started_on_reset'].includes(
        field
      )
    ) {
      groups.options.push(problem);
    } else {
      groups.general.push(problem);
    }
  });
  return groups;
};

export const formatDateTime = (value) =>
  value ? format(parseISO(value), 'd MMM yyyy, HH:mm') : '–';

export const plural = (count, one, many = `${one}s`) =>
  `${Number(count ?? 0).toLocaleString()} ${count === 1 ? one : many}`;

export const formatPercent = (value) =>
  value === null || value === undefined ? '–' : `${Math.round(value)}%`;
